import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { LoginResponse, ProblemDetail } from '@/types/api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

/**
 * The access token lives in memory only.
 *
 * Keeping it out of localStorage means an XSS payload cannot simply read it back; the
 * refresh token is an httpOnly cookie the browser will not hand to JavaScript at all, so
 * a page reload recovers the session through /auth/refresh rather than from storage.
 */
let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

export const setUnauthenticatedHandler = (handler: (() => void) | null) => {
  onUnauthenticated = handler;
};

export const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  // the refresh cookie must ride along on /auth/refresh and /auth/logout
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

/**
 * A single in-flight refresh shared by every request that hits a 401 at once, so a page
 * with six parallel queries performs one rotation rather than six competing ones (which
 * the server would treat as token reuse and punish by revoking the family).
 */
let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = async (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post<LoginResponse>(`${BASE_URL}/auth/refresh`, null, { withCredentials: true })
      .then((response) => {
        setAccessToken(response.data.accessToken);
        return response.data.accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ProblemDetail>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const status = error.response?.status;
    const isAuthCall = original?.url?.includes('/auth/');

    if (status === 401 && original && !original._retried && !isAuthCall) {
      original._retried = true;
      try {
        const token = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      } catch {
        setAccessToken(null);
        onUnauthenticated?.();
      }
    }
    return Promise.reject(error);
  },
);

/** Extracts the human-readable message from an RFC 7807 body. */
export const problemMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError<ProblemDetail>(error)) {
    const problem = error.response?.data;
    if (problem?.detail) return problem.detail;
    if (problem?.title) return problem.title;
    if (error.message) return error.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
};

/** The machine-readable code, for branching on a specific failure. */
export const problemCode = (error: unknown): string | undefined => {
  if (axios.isAxiosError<ProblemDetail>(error)) {
    return error.response?.data?.code;
  }
  return undefined;
};

export const isConflict = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 409;
